'use client';

import { ResponseFormat, targetConfig } from '@decodo/utils';

import { useTranslation } from '@/lib/i18n/i18n.client';

import { requestLanguages } from '@/blocks/Scraper/constants/languages';
import {
  ResponseFormatView,
  responseFormatViews,
} from '@/blocks/Scraper/constants/response-formats';
import { useScraper } from '@/blocks/Scraper/state/context';
import CopyButton from '@/components/atoms/CodeBlock/CopyButton';
import { Toggle } from '@/components/atoms/Toggle';

type LangToggleProps = {
  activeTab: 'request' | 'response';
  requestLanguageIndex: number;
  setRequestLanguageIndex: React.Dispatch<React.SetStateAction<number>>;
  responseFormatIndex: number;
  setResponseFormatIndex: React.Dispatch<React.SetStateAction<number>>;
  copyText?: string;
  hasResponse: boolean;
};

export const LangToggle = ({
  requestLanguageIndex,
  responseFormatIndex,
  setRequestLanguageIndex,
  setResponseFormatIndex,
  hasResponse,
  copyText,
  activeTab,
}: LangToggleProps) => {
  const { texts } = useTranslation();

  const responseFormatMap: Record<
    ResponseFormat,
    Record<ResponseFormatView, string>
  > = {
    json: {
      rich: texts.scraper.format_table,
      text: texts.scraper.format_json,
    },
    html: {
      rich: texts.scraper.format_live_preview,
      text: texts.scraper.format_html,
    },
  };

  const { target } = useScraper();

  const responseFormat = targetConfig[target.targetKey]?.format || 'json';
  const formatOptions = responseFormatViews.map(
    (view) => responseFormatMap[responseFormat][view]
  );

  const shouldDisplayCopyButton =
    activeTab === 'request' || (activeTab === 'response' && hasResponse);

  const shouldDisplayResponseFormatToggle =
    activeTab === 'response' && hasResponse && responseFormat === 'json';
  return (
    <div className='dark relative flex shrink-0 justify-between py-4'>
      <div className='overflow-hidden'>
        {activeTab === 'request' && (
          <Toggle
            options={requestLanguages as unknown as string[]}
            size='s'
            classNames={{
              toggleItem: {
                button: 'font-mono text-body-m',
                selectedButton: '!text-body-m-bold',
              },
            }}
            selectedIndex={requestLanguageIndex}
            onValueChange={setRequestLanguageIndex}
            responsive={false}
          />
        )}

        {shouldDisplayResponseFormatToggle && (
          <Toggle
            options={formatOptions}
            size='s'
            selectedIndex={responseFormatIndex}
            onValueChange={setResponseFormatIndex}
            responsive={false}
          />
        )}
      </div>
      {shouldDisplayCopyButton && (
        <CopyButton
          classNames={{
            root: 'h-4 w-4 align-middle mr-2',
            copyIcon: 'h-4 w-4',
            successIcon: 'h-4 w-4',
          }}
          copyText={copyText || ''}
        />
      )}
    </div>
  );
};
